import { DedupRegistry } from './core/dedupe.js';
import { SessionManager } from './core/session.js';
import { Transport } from './core/transport.js';
import { Tracker } from './core/tracker.js';
import { createEnvelope, TRACKER_VERSION } from './core/envelope.js';
import { createLogger } from './core/logger.js';
import { resolveSelectors } from './core/selectors.js';
import { collectPageView } from './collectors/page_view.js';
import { collectDiagnostics } from './collectors/diagnostics.js';
import { collectCommerce } from './collectors/commerce.js';
import { collectLanding } from './collectors/landing.js';
import { collectOffers } from './collectors/offers.js';
import { collectSubscriptionPopup } from './collectors/subscription.js';
import { collectQuiz } from './collectors/quiz.js';
import { collectAccount } from './collectors/account.js';

/**
 * Bootstraps the browser side of the tracker.
 *
 * All page classification comes from PHP (`trackdaddyData.context`) — the browser
 * cannot tell a landing one-pager from the real checkout page, which is exactly the
 * mistake that made v1 fire initiate_checkout for 71% of visitors.
 */

const SDK_TIMEOUT_MS = 5000;

const DEFAULT_CONTEXT = {
  page_type: 'other',
  page_family: 'content',
  page_path: '/',
  landing_id: null,
  store_id: '',
  env: 'production',
  session_ttl: 1800,
  collect_url: 'https://connect.locus.it.com/v1/collect',
  scroll_depth: false,
  debug: false,
};

export function boot(win = window, doc = document) {
  // A second copy of the bundle would give us two Trackers, two SessionManagers racing the
  // same `_td_ses` cookie, and two of every event — and the once-per-session guards cannot
  // help, because both instances race the same storage key. Any cache or optimizer that
  // inlines or concatenates scripts is enough to cause it. Tracker.listen and the
  // diagnostics transport patch already guard themselves the same way.
  if (win.trackdaddy) {
    return win.trackdaddy;
  }

  const data = win.trackdaddyData || {};
  const context = Object.assign({}, DEFAULT_CONTEXT, data.context || {});
  const selectors = resolveSelectors(context.selectors);

  // `context.debug` is the server's final answer: the setting is on AND this visitor is
  // an administrator. The browser never re-decides it.
  const logger = createLogger(Boolean(context.debug), win);

  const session = new SessionManager({ ttlMs: sessionTtlMs(context.session_ttl), doc });
  // Read before any event can create the session, otherwise page_view never sees a
  // new one — the diagnostics collector runs first and touches it.
  const isEntry = session.touch().isNew;
  const dedupe = new DedupRegistry();
  const transport = new Transport({
    collectUrl: context.collect_url,
    pixelId: data.pixelId || null,
    win,
    doc,
    // Until now nothing passed this, so a rejected /v1/collect POST was invisible in the
    // browser — and a 4xx there is the only delivery failure the browser can ever see.
    onError: (error) => logger.warn('transport error', error),
  });
  const envelope = createEnvelope({ context, session, win });
  const tracker = new Tracker({
    context,
    session,
    dedupe,
    transport,
    envelope,
    logger,
    win,
  });

  tracker.listen(doc);
  watchSdk(tracker, transport, win, doc, context, logger);

  win.trackdaddy = {
    track: (name, params, options) => tracker.track(name, params, options),
    identify: (payload) => tracker.identify(payload),
    session: () => session.touch(),
    debugServerEvents: (events) => logServerEvents(logger, events),
    context,
    selectors,
    tracker,
  };

  if (logger.enabled) {
    logger.group('ready', {
      page_type: context.page_type,
      page_family: context.page_family,
      landing_id: context.landing_id,
      store_id: context.store_id,
      env: context.env,
      session_id: session.touch().id,
      is_entry: isEntry,
      tracker_version: TRACKER_VERSION,
      sdk: transport.sdkReady() ? 'ready' : 'pending',
      scroll_depth: Boolean(context.scroll_depth),
    });
  }

  // Diagnostics first: they are what reports a failure in everything below.
  run(tracker, logger, 'diagnostics', () => collectDiagnostics(tracker, { win, doc }));
  run(tracker, logger, 'page_view', () => collectPageView(tracker, { doc, win, isEntry }));
  run(tracker, logger, 'commerce', () => collectCommerce(tracker, { win, doc, data, selectors }));
  run(tracker, logger, 'landing', () => collectLanding(tracker, { win, doc, selectors }));
  run(tracker, logger, 'offers', () => collectOffers(tracker, { win, doc, selectors }));
  run(tracker, logger, 'subscription', () => collectSubscriptionPopup(tracker, { win, doc, selectors, data }));
  run(tracker, logger, 'quiz', () => collectQuiz(tracker, { win, doc }));
  run(tracker, logger, 'account', () => collectAccount(tracker, { win, doc, selectors }));

  if (typeof win.CustomEvent === 'function') {
    doc.dispatchEvent(new win.CustomEvent('trackdaddy:ready', { detail: { context, tracker } }));
  }

  return win.trackdaddy;
}

/**
 * A misconfigured TTL would otherwise make every event start its own session.
 */
function sessionTtlMs(configured) {
  const seconds = Number(configured);

  return (Number.isFinite(seconds) && seconds > 0 ? seconds : DEFAULT_CONTEXT.session_ttl) * 1000;
}

/**
 * One broken collector must never take the rest of the tracking down with it — and the
 * failure itself is a js_error worth reporting, named after the collector that raised
 * it.
 */
function run(tracker, logger, collector, fn) {
  try {
    fn();
  } catch (error) {
    logger.warn('collector failed', collector, error);

    tracker.track('js_error', {
      message: String((error && error.message) || error).slice(0, 500),
      source_file: 'collectors/' + collector + '.js',
      line: 0,
      error_type: 'collector',
    });
  }
}

/**
 * Events the PHP half dispatched, handed over by the inline script Frontend\Assets
 * prints in the footer. They are already on their way to /v1/collect — this is a mirror
 * for the console, not a delivery path.
 */
function logServerEvents(logger, events) {
  if (!logger.enabled || !Array.isArray(events) || events.length === 0) {
    return;
  }

  events.forEach((event) => {
    logger.group('server ' + event.event, { via: 'php', ts: event.ts, properties: event.properties });
  });
}

/**
 * pixel_load_failed (spec §3) and the switch to direct delivery.
 *
 * The loader stub queues calls until the SDK upgrades it. If the script never arrives
 * — adblocker, DNS failure, in-app WebView — those calls would sit in memory forever,
 * so the transport re-sends them to the collect endpoint itself.
 */
function watchSdk(tracker, transport, win, doc, context, logger) {
  let handled = false;

  const fail = (reason) => {
    if (handled) {
      return;
    }
    handled = true;

    logger.warn('SDK unavailable (' + reason + ') — switching to direct delivery');

    transport.markSdkFailed();

    tracker.track(
      'pixel_load_failed',
      {
        script_name: 'caigoevents.js',
        failure_reason: reason,
        page_path: context.page_path,
      },
      { once: 'session', dedupeKey: 'pixel_load_failed:' + reason }
    );
  };

  if (win.__trackdaddy_sdk_failed) {
    fail(String(win.__trackdaddy_sdk_failed));

    return;
  }

  doc.addEventListener('trackdaddy:sdk-failed', (event) => {
    fail((event && event.detail && event.detail.reason) || 'onerror');
  });

  win.setTimeout(() => {
    if (!transport.sdkReady()) {
      fail('timeout');
    } else {
      // The SDK drained the stub queue itself; our copies would only be duplicates.
      transport.markSdkReady();
    }
  }, SDK_TIMEOUT_MS);
}

// boot() is idempotent; the `!window.trackdaddy` term just avoids the call entirely when a
// second copy of the bundle parses.
if (typeof window !== 'undefined' && !window.__trackdaddy_test_mode && !window.trackdaddy) {
  boot(window, document);
}
